import { useMemo } from 'react';
import { useNodes, useReactFlow } from '@xyflow/react';
import {
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { API, QUERY_KEYS } from '@config';

import {
  useRequest,
  useDisplayResponseMessage,
  useActiveOrganization,
} from '@hooks';
import { AppNode, NodeData } from '../types/node';

export const useCreateRootNode = (flow: Flow) => {
  const request = useRequest();
  const queryClient = useQueryClient();
  const displayResponseMessage = useDisplayResponseMessage();

  return async () => {
    try {
      await request(API.FLOWS_BUILDER_CREATE_ROOT_NODE(flow.uuid), {
        method: 'POST',
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FLOW_SCHEMA] });
    } catch (error) {
      console.error(error);
      displayResponseMessage('flows.schema.create_root_node.error');
    }
  };
};

export const useCreateNestedNode = (parentNode: NodeData) => {
  const request = useRequest();
  const queryClient = useQueryClient();
  const displayResponseMessage = useDisplayResponseMessage();

  return async () => {
    try {
      await request(API.FLOWS_BUILDER_CREATE_NESTED_NODE(parentNode.uuid), {
        method: 'POST',
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FLOW_SCHEMA] });
    } catch (error) {
      console.error(error);
      displayResponseMessage('flows.schema.create_nested_node.error');
    }
  };
};

export const useDeleteNode = (node: NodeData) => {
  const request = useRequest();
  const queryClient = useQueryClient();
  const displayResponseMessage = useDisplayResponseMessage();

  return async () => {
    try {
      await request(API.FLOWS_BUILDER_DELETE_NODE(node.uuid), {
        method: 'DELETE',
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FLOW_SCHEMA] });
    } catch (error) {
      console.error(error);
      displayResponseMessage('flows.schema.delete_node.error');
    }
  };
};

export const useConnectNodes = () => {
  const request = useRequest();
  const queryClient = useQueryClient();
  const displayResponseMessage = useDisplayResponseMessage();

  return async (sourceNodeUuid: string, targetNodeUuid: string) => {
    try {
      await request(API.FLOWS_BUILDER_CONNECT_NODES(sourceNodeUuid, targetNodeUuid), {
        method: 'POST',
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FLOW_SCHEMA] });
    } catch (error) {
      console.error(error);
      displayResponseMessage('flows.schema.connect_nodes.error');
    }
  };
};

export const useDeleteEdge = () => {
  const request = useRequest();
  const queryClient = useQueryClient();
  const displayResponseMessage = useDisplayResponseMessage();

  return async (sourceNodeUuid: string, targetNodeUuid: string) => {
    try {
      await request(API.FLOWS_BUILDER_DELETE_EDGE(sourceNodeUuid, targetNodeUuid), {
        method: 'POST',
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FLOW_SCHEMA] });
    } catch (error) {
      console.error(error);
      displayResponseMessage('flows.schema.delete_edge.error');
    }
  };
};

export const useInsertNodeBetween = () => {
  const request = useRequest();
  const queryClient = useQueryClient();
  const displayResponseMessage = useDisplayResponseMessage();
  const { getNodes, setNodes } = useReactFlow();

  return async (sourceNodeUuid: string, targetNodeUuid: string) => {
    try {
      // Get current nodes to calculate positions
      const currentNodes = getNodes();
      const sourceNode = currentNodes.find(n => n.id === sourceNodeUuid);
      const targetNode = currentNodes.find(n => n.id === targetNodeUuid);
      
      // Calculate the horizontal shift needed (space for new node + padding)
      const HORIZONTAL_SHIFT = 300; // Adjust this value based on your node width + spacing

      // Make the API call to insert the node
      await request(API.FLOWS_BUILDER_INSERT_NODE_BETWEEN(sourceNodeUuid, targetNodeUuid), {
        method: 'POST',
      });

      // Shift target node and all its descendants to the right
      if (sourceNode && targetNode) {
        setNodes((nodes) => {
          return nodes.map((node) => {
            // Check if this node should be shifted (target node or any node to its right)
            if (node.position.x >= targetNode.position.x) {
              return {
                ...node,
                position: {
                  ...node.position,
                  x: node.position.x + HORIZONTAL_SHIFT,
                },
              };
            }
            return node;
          });
        });
      }

      // Invalidate queries to fetch the new node
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FLOW_SCHEMA] });
    } catch (error) {
      console.error(error);
      displayResponseMessage('flows.schema.insert_node.error');
    }
  };
};

export const useSetNodeType = (node: NodeData) => {
  const request = useRequest();
  const queryClient = useQueryClient();
  const displayResponseMessage = useDisplayResponseMessage();

  return async (nodeTypeUuid: string) => {
    try {
      await request(API.FLOWS_BUILDER_SET_NODE_TYPE(node.uuid, nodeTypeUuid), {
        method: 'PATCH',
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FLOW_SCHEMA] });
    } catch (error) {
      console.error(error);
      displayResponseMessage('nodes.type.set.error');
    }
  };
};

export const useSetNodeAction = (node: NodeData) => {
  const request = useRequest();
  const queryClient = useQueryClient();
  const displayResponseMessage = useDisplayResponseMessage();

  return async (nodeActionUuid: string) => {
    try {
      await request(API.FLOWS_BUILDER_SET_NODE_ACTION(node.uuid, nodeActionUuid), {
        method: 'PATCH',
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FLOW_SCHEMA] });
    } catch (error) {
      console.error(error);
      displayResponseMessage('nodes.action.set.error');
    }
  };
};

export const useSetNodeInput = (node: NodeData) => {
  const request = useRequest();
  const queryClient = useQueryClient();
  const displayResponseMessage = useDisplayResponseMessage();

  return async (input: Record<string, unknown>) => {
    try {
      await request(API.FLOWS_BUILDER_SET_NODE_INPUT(node.uuid), {
        method: 'PATCH',
        data: { input },
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FLOW_SCHEMA] });
    } catch (error) {
      console.error(error);
      displayResponseMessage('nodes.input.set.error');
    }
  };
};

export const useSetNodeTitle = (node: NodeData) => {
  const request = useRequest();
  const queryClient = useQueryClient();
  const displayResponseMessage = useDisplayResponseMessage();

  return async (title: string) => {
    try {
      await request(API.FLOWS_BUILDER_SET_NODE_TITLE(node.uuid), {
        method: 'PATCH',
        data: { title },
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FLOW_SCHEMA] });
    } catch (error) {
      console.error(error);
      displayResponseMessage('nodes.title.set.error');
    }
  };
};

export const useNodeConnectionOptions = (nodeType?: NodeType) => {
  const request = useRequest();
  const displayResponseMessage = useDisplayResponseMessage();
  const organization = useActiveOrganization();

  const { data } = useQuery({
    enabled: !!(nodeType && organization?.uuid),
    queryKey: [
      QUERY_KEYS.CONNECTIONS,
      QUERY_KEYS.NODE_CONNECTION_OPTIONS,
      nodeType?.uuid,
      organization?.uuid,
    ],
    queryFn: async () => {
      try {
        const { data } = await request(
          API.CONNECTIONS_NODETYPE_LIST(
            nodeType?.uuid as string,
            organization?.uuid as string,
          ),
        );
        return data as Connection[];
      } catch (error) {
        console.error(error);
        displayResponseMessage('nodes.connection.fetch.error');
        return null;
      }
    },
    staleTime: Infinity,
  });

  return data;
};

export const useSetNodeConnection = (node: NodeData) => {
  const request = useRequest();
  const queryClient = useQueryClient();
  const displayResponseMessage = useDisplayResponseMessage();

  return async (nodeConnectionUuid: string) => {
    try {
      await request(
        API.FLOWS_BUILDER_SET_NODE_CONNECTION(node.uuid, nodeConnectionUuid),
        {
          method: 'PATCH',
        },
      );
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FLOW_SCHEMA] });
    } catch (error) {
      console.error(error);
      displayResponseMessage('nodes.connection.set.error');
    }
  };
};

type NodeActionInputFieldsResponse = {
  fields: FieldConfiguration[];
}

export const useNodeActionInputFields = (
  nodeAction?: NodeAction,
): FieldConfiguration[] => {
  const request = useRequest();
  const displayResponseMessage = useDisplayResponseMessage();

  const { data } = useQuery({
    enabled: !!nodeAction,
    queryKey: [QUERY_KEYS.NODE_ACTION_INPUT_FIELDS, nodeAction?.uuid],
    queryFn: async () => {
      try {
        const { data } = await request<NodeActionInputFieldsResponse>(
          API.NODETYPES_ACTIONS_INPUT(nodeAction?.uuid as string),
        );
        return data.fields;
      } catch (error) {
        console.error(error);
        displayResponseMessage('nodes.action.fetch.error');
        return null;
      }
    },
    staleTime: Infinity,
  });

  return data || [];
};

export const useSelectedNode = (): AppNode | null => {
  const nodes = useNodes<AppNode>();
  return (
    useMemo(() => {
      return nodes.find((node) => node.selected === true);
    }, [nodes]) || null
  );
};

export const useOutputFieldsMap = (): Map<AppNode, FieldConfiguration[]> => {
  const request = useRequest();
  const displayResponseMessage = useDisplayResponseMessage();

  const nodes = useNodes<AppNode>();
  const selectedNode = useSelectedNode();

  // To get relevant output fields, we need to fetch output fields
  // of all parent nodes that have an action, these are the 'relevantNodes'
  const relevantNodes = useMemo(() => {
    if (!selectedNode) return [];
    const _relevantNodes = [];
    let currentNode: AppNode | null = selectedNode;
    while (currentNode?.data.parent) {
      const parentNode =
        nodes.find((node) => node.data.uuid === currentNode?.data.parent) || null;
      if (parentNode?.data.nodeAction) {
        _relevantNodes.push(parentNode);
      }
      currentNode = parentNode;
    }
    return _relevantNodes;
  }, [nodes, selectedNode]);

  // Construct output field query for each relevant parent node
  const queries = useMemo(() => {
    return relevantNodes.map((node) => {
      const action = node.data.nodeAction as NodeAction;
      return {
        queryKey: [QUERY_KEYS.NODE_ACTION_OUTPUT_FIELDS, action.uuid],
        queryFn: async () => {
          try {
            const { data } = await request<NodeActionInputFieldsResponse>(API.NODETYPES_ACTIONS_OUTPUT(action.uuid));
            return data.fields;
          } catch (error) {
            console.error(error);
            displayResponseMessage('nodes.action.fetch.error');
            return null;
          }
        },
        staleTime: Infinity,
      };
    });
  }, [request, displayResponseMessage, relevantNodes]);

  type OutputFieldsQueryResult = ReturnType<typeof useQuery<FieldConfiguration[]>>;

  const results = useQueries({
    queries,
  }) as OutputFieldsQueryResult[];

  // Construct output fields map, use Map to ensure consistent key order
  const outputFieldsMap = useMemo(() => {
    const _outputFieldsMap = new Map<AppNode, FieldConfiguration[]>();
    results.forEach((result, index) => {
      const node = relevantNodes[index];
      if (result.isSuccess && Array.isArray(result.data) && result.data.length > 0) {
        _outputFieldsMap.set(node, result.data as FieldConfiguration[]);
      }
    });
    return _outputFieldsMap;
  }, [relevantNodes, results]);

  return outputFieldsMap;
};
